import { baseApi } from '@app/baseApi';

import type {
  CreateTransformerRequest,
  ExecutionDetails,
  ExecutionLog,
  IntegrationType,
  Transformer,
  TransformerVersion,
  Trigger,
  TriggerDetailsRequest,
  TriggerDetailsResponse,
} from '@/types';
import type { ScheduleSummary } from '@/types/schedule';

import { toUpdateTransformerInfoPayload } from '../mappers/transformers.mapper';
import {
  type Deployment,
  transformGetAdoptedIntegrationTypesResponse,
  transformGetAppDeploymentsResponse,
  transformGetExecutionDetailsResponse,
  transformGetExecutionMetadataResponse,
  transformGetExecutionStepsResponse,
  transformGetIntegrationTypesResponse,
  transformGetTransformerLogsResponse,
  transformGetTransformerVersionsResponse,
  transformGetTransformersResponse,
  transformGetTriggerDetailsResponse,
  transformGetTriggerSummaryResponse,
  transformGetTriggersResponse,
  transformGetWorkspaceExecutionsResponse,
  transformSaveTransformerRequest,
} from './transformers.mappers';

export interface TransformerDeployment {
  transId: string;
  environmentId: string;
}

const normalizeExecutionCount = (response: unknown): { total: number } => {
  if (typeof response === 'number') return { total: response };
  if (response && typeof response === 'object') {
    const record = response as Record<string, unknown>;
    const value = record.total ?? record.count;
    return { total: Number(value) || 0 };
  }
  return { total: Number(response) || 0 };
};

export const transformersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get available integration types
    getIntegrationTypes: builder.query<IntegrationType[], void>({
      query: () => ({
        url: 'integrationtypes/available',
        endpoint: 'transformer',
      }),
      transformResponse: transformGetIntegrationTypesResponse,
    }),

    // Get adopted integration types for tenant
    getAdoptedIntegrationTypes: builder.query<IntegrationType[], string>({
      query: (tenantId) => ({
        url: `${tenantId}/integrationtypes/adopted`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetAdoptedIntegrationTypesResponse,
    }),

    // Get transformers for an app
    getTransformers: builder.query<Transformer[], string>({
      query: (appId) => ({
        url: `transformers/${appId}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetTransformersResponse,
      providesTags: (result, _error, appId) =>
        result
          ? [
              ...result.map(({ baseId }) => ({ type: 'Recipe' as const, id: baseId })),
              { type: 'Recipe', id: `app-${appId}` },
            ]
          : [{ type: 'Recipe', id: `app-${appId}` }],
    }),

    // Get transformer versions
    getTransformerVersions: builder.query<
      TransformerVersion[],
      { transBaseId: string; appId: string }
    >({
      query: ({ transBaseId, appId }) => ({
        url: `transformer/versions/${transBaseId}?appId=${appId}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetTransformerVersionsResponse,
      providesTags: (_result, _error, { transBaseId }) => [{ type: 'Recipe', id: transBaseId }],
    }),

    // Create/Update transformer
    saveTransformer: builder.mutation<
      Transformer,
      { appId: string; data: CreateTransformerRequest & { Id?: string } }
    >({
      query: ({ appId, data }) => ({
        url: `transformers/create/${appId}`,
        endpoint: 'transformer',
        method: 'POST',
        body: transformSaveTransformerRequest(data),
      }),
      transformResponse: (response) => {
        // saveTransformer returns single transformer, use array transformer and get first
        const result = transformGetTransformersResponse([response]);
        return result[0];
      },
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'Recipe', id: `app-${appId}` }],
    }),

    // Update transformer with JSON config
    updateTransformerWithJSON: builder.mutation<
      void,
      { appId: string; transBaseId: string; data: Record<string, unknown> }
    >({
      query: ({ appId, data }) => ({
        url: `transformers/create/${appId}?configType=json`,
        endpoint: 'transformer',
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, { appId, transBaseId }) => [
        { type: 'Recipe', id: `app-${appId}` },
        { type: 'Recipe', id: transBaseId },
      ],
    }),

    // Create transformer from template
    createFromTemplate: builder.mutation<
      Transformer,
      { tenantId: string; appId: string; templateId: string }
    >({
      query: ({ tenantId, appId, templateId }) => ({
        url: `${tenantId}/${appId}/transformers/template/${templateId}`,
        endpoint: 'transformer',
        method: 'POST',
      }),
      transformResponse: (response) => {
        // createFromTemplate returns single transformer, use array transformer and get first
        const result = transformGetTransformersResponse([response]);
        return result[0];
      },
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'Recipe', id: `app-${appId}` }],
    }),

    // Get transformer event/context data
    getTransformerEventData: builder.query<unknown, string>({
      query: (transBaseId) => ({
        url: `transformers/${transBaseId}/eventdata`,
        endpoint: 'transformer',
      }),
    }),

    // Post transformer event/context data
    postTransformerEventData: builder.mutation<
      void,
      { transBaseId: string; data: Record<string, unknown> }
    >({
      query: ({ transBaseId, data }) => ({
        url: `transformers/${transBaseId}/eventdata`,
        endpoint: 'transformer',
        method: 'POST',
        body: data,
      }),
    }),

    // Get transformer DPT emails
    // API returns array of objects: [{email: "user@example.com"}, ...]
    getTransformerEmails: builder.query<{ emails: string[] }, string>({
      query: (transBaseId) => ({
        url: `transformers/${transBaseId}/dpt-emails`,
        endpoint: 'transformer',
      }),
      transformResponse: (response: unknown): { emails: string[] } => {
        if (Array.isArray(response)) {
          return {
            emails: response
              .map((item: { email?: string }) => item.email)
              .filter((e): e is string => !!e),
          };
        }
        return { emails: [] };
      },
      providesTags: (_result, _error, transBaseId) => [
        { type: 'Recipe', id: `emails-${transBaseId}` },
      ],
    }),

    // Update transformer emails
    updateTransformerEmails: builder.mutation<void, { transBaseId: string; emails: string[] }>({
      query: ({ transBaseId, emails }) => ({
        url: `transformers/${transBaseId}/manage-dpt-emails`,
        endpoint: 'transformer',
        method: 'PUT',
        body: { emails },
        responseHandler: 'text',
      }),
      invalidatesTags: (_result, _error, { transBaseId }) => [
        { type: 'Recipe', id: `emails-${transBaseId}` },
      ],
    }),

    // Update transformer metadata (for Properties modal)
    updateTransformerInfo: builder.mutation<
      void,
      {
        appId: string;
        transId: string;
        data: {
          name: string;
          config: string;
          tag: string; // Frontend uses "tag" (maps to "environ")
          version: number;
          baseId: string; // UUID string, maps to "baseid"
          id: number;
          description: string;
          languageVersion: string;
          processingType: string;
          maxDataProcTime: string;
        };
      }
    >({
      query: ({ appId, data }) => ({
        url: `transformers/create/${appId}`,
        endpoint: 'transformer',
        method: 'POST',
        body: toUpdateTransformerInfoPayload(data),
        responseHandler: 'text',
      }),
      invalidatesTags: (_result, _error, { appId, transId }) => [
        { type: 'Recipe', id: `app-${appId}` },
        { type: 'Recipe', id: transId },
      ],
    }),

    // Get triggers for transformer
    getTriggers: builder.query<Trigger[], string>({
      query: (transBaseId) => ({
        url: `gettriggers/${transBaseId}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetTriggersResponse,
    }),

    // Get all trigger subscriptions for app
    getAllTriggers: builder.query<Trigger[], string>({
      query: (appId) => ({
        url: `getalltriggers/${appId}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetTriggersResponse,
    }),

    // Get trigger summary
    getTriggerSummary: builder.query<
      ScheduleSummary[],
      { tenantId: string; appId: string; timeZone: string; envId?: string }
    >({
      query: ({ tenantId, appId, timeZone, envId }) => ({
        url: `tenants/${tenantId}/apps/${appId}/getTriggerSummary?timeZone=${encodeURIComponent(timeZone)}${envId ? `&envId=${envId}` : ''}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetTriggerSummaryResponse,
      providesTags: ['Schedule'],
    }),

    // Get trigger details
    getTriggerDetails: builder.query<
      TriggerDetailsResponse,
      TriggerDetailsRequest & { tenantId: string; appId: string }
    >({
      query: ({
        tenantId,
        appId,
        startDate,
        endDate,
        envId,
        timeZone,
        currentPage = 1,
        pageSize = 10,
      }) => {
        const paginationParams =
          currentPage === 0 && pageSize === 0
            ? ''
            : `&currentPage=${currentPage}&pageSize=${pageSize}`;

        return {
          url: `tenants/${tenantId}/apps/${appId}/getTriggerDetails?startDate=${startDate}&endDate=${endDate}${paginationParams}${envId ? `&envId=${envId}` : ''}${timeZone ? `&timeZone=${encodeURIComponent(timeZone)}` : ''}`,
          endpoint: 'transformer',
        };
      },
      transformResponse: transformGetTriggerDetailsResponse,
      providesTags: ['Schedule'],
    }),

    // Save/create trigger
    // API expects: baseid (int), environmentId (camelCase int), isrecurring, templateName, + schedule config
    saveTrigger: builder.mutation<void, { tenantId: string; data: Record<string, unknown> }>({
      query: ({ tenantId, data }) => ({
        url: `${tenantId}/createtrigger`,
        endpoint: 'transformer',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'Recipe', id: String(data.baseid || '') },
        'Schedule',
      ],
    }),

    // Clear trigger subscription
    clearTrigger: builder.mutation<void, { envId: string; transBaseId: string }>({
      query: ({ envId, transBaseId }) => ({
        url: `environments/${envId}/cleartrigger/${transBaseId}`,
        endpoint: 'transformer',
        method: 'GET', // Legacy uses GET for this
      }),
      invalidatesTags: (_result, _error, { transBaseId }) => [
        { type: 'Recipe', id: transBaseId },
        'Schedule',
      ],
    }),

    // Get transformer logs
    getTransformerLogs: builder.query<
      ExecutionLog[],
      {
        tenantId: string;
        transBaseId: string;
        limit?: number;
        offset?: number;
        state?: string;
        duration?: string;
      }
    >({
      query: ({ tenantId, transBaseId, limit = 50, offset = 0, state, duration }) => {
        let url = `${tenantId}/logs/transformers/${transBaseId}?limit=${limit}&offset=${offset}`;
        if (state) url += `&state=${state}`;
        if (duration) url += `&duration=${duration}`;
        return { url, endpoint: 'transformer' };
      },
      transformResponse: transformGetTransformerLogsResponse,
    }),

    // Get execution count
    getExecutionCount: builder.query<
      { total: number },
      { tenantId: string; appId: string; transId: string }
    >({
      query: ({ tenantId, appId, transId }) => ({
        url: `${tenantId}/${appId}/count/executions/${transId}`,
        endpoint: 'transformer',
      }),
      transformResponse: normalizeExecutionCount,
    }),

    // Get execution details
    getExecutionDetails: builder.query<ExecutionDetails, string>({
      query: (transId) => ({
        url: `trans/executions/${transId}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetExecutionDetailsResponse,
    }),

    // Get config templates (data mechanics)
    getConfigTemplates: builder.query<unknown[], void>({
      query: () => ({
        url: 'config-templates',
        endpoint: 'transformer',
      }),
    }),

    // Get workspace executions (all recipes in an app)
    getWorkspaceExecutions: builder.query<
      ExecutionLog[],
      {
        tenantId: string;
        appId: string;
        limit?: number;
        offset?: number;
        state?: string;
        duration?: string;
        startTime?: string;
        endTime?: string;
      }
    >({
      query: ({ tenantId, appId, limit, offset, state, duration, startTime, endTime }) => {
        // Use different endpoints based on whether filtering is needed
        // Legacy: without filters uses logs/transformers/app, with filters uses logs/filtered/transformers/app
        const hasFilters = state || duration || startTime || endTime;
        const basePath = hasFilters ? 'logs/filtered/transformers/app' : 'logs/transformers/app';

        const parts: string[] = [];
        if (duration) parts.push(`duration=${duration}`);
        if (state) parts.push(`state=${state}`);
        if (startTime) parts.push(`startTime=${encodeURIComponent(startTime)}`);
        if (endTime) parts.push(`endTime=${encodeURIComponent(endTime)}`);
        if (limit !== undefined) parts.push(`limit=${limit}`);
        if (offset !== undefined) parts.push(`offset=${offset}`);

        const url = `${tenantId}/${basePath}/${appId}${parts.length ? `?${parts.join('&')}` : ''}`;
        return { url, endpoint: 'transformer' };
      },
      transformResponse: transformGetWorkspaceExecutionsResponse,
    }),

    // Get workspace execution count
    getWorkspaceExecutionCount: builder.query<
      { total: number },
      { tenantId: string; appId: string }
    >({
      query: ({ tenantId, appId }) => ({
        url: `${tenantId}/${appId}/count/executions`,
        endpoint: 'transformer',
      }),
      transformResponse: normalizeExecutionCount,
    }),

    // Get execution steps/details by GUID
    getExecutionSteps: builder.query<unknown[], { tenantId: string; executionGuid: string }>({
      query: ({ tenantId, executionGuid }) => ({
        url: `${tenantId}/logs/steps/externalExecutionId/${executionGuid}`,
        endpoint: 'transformer',
      }),
    }),

    // Get execution metadata by GUID
    getExecutionMetadataByGuid: builder.query<
      ExecutionLog[],
      { tenantId: string; executionGuid: string }
    >({
      query: ({ tenantId, executionGuid }) => ({
        url: `${tenantId}/logs/transformers/externalExecutionId/${executionGuid}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetExecutionMetadataResponse,
    }),

    // Get execution log steps by GUID (for detailed view page)
    getExecutionLogsByGuid: builder.query<unknown[], { tenantId: string; executionGuid: string }>({
      query: ({ tenantId, executionGuid }) => ({
        url: `${tenantId}/logs/steps/externalExecutionId/${executionGuid}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetExecutionStepsResponse,
    }),

    // Get execution by external GUID
    getExecutionByGuid: builder.query<ExecutionLog, { tenantId: string; executionGuid: string }>({
      query: ({ tenantId, executionGuid }) => ({
        url: `${tenantId}/logs/transformers/externalExecutionId/${executionGuid}`,
        endpoint: 'transformer',
      }),
    }),

    // Execute transformer
    executeTransformer: builder.mutation<boolean, { transId: string; queryParams: string }>({
      query: ({ transId, queryParams }) => ({
        url: `execute/${transId}?${queryParams}`,
        endpoint: 'transformer',
        method: 'GET', // Legacy uses GET for execute
      }),
    }),

    // Stop execution
    stopExecution: builder.mutation<void, { envId: string; transId: string }>({
      query: ({ envId, transId }) => ({
        url: `stopexecute/${envId}/${transId}`,
        endpoint: 'transformer',
        method: 'GET', // Legacy uses GET
      }),
    }),

    // Get presigned S3 URL for execution logs download
    getExecutionLogsDownloadUrl: builder.query<
      string,
      {
        tenantId: string;
        appId: string;
        envId: string;
        date: string;
        recipeId: string;
        fileName: string;
      }
    >({
      query: ({ tenantId, appId, envId, date, recipeId, fileName }) => ({
        url: `${tenantId}/${appId}/${envId}/executionlogs/${date}/${recipeId}/${fileName}`,
        endpoint: 'transformer',
        // Backend returns presigned S3 URL as JSON-encoded string
        // Returns 201 Created on success, 404 Not Found if file doesn't exist
      }),
    }),

    // Get deployments for transformer
    getTransformerDeployments: builder.query<
      TransformerDeployment[],
      { appId: string; baseId: string }
    >({
      query: ({ appId, baseId }) => ({
        url: `${appId}/transformer/${baseId}/deployments`,
        endpoint: 'transformer',
      }),
      providesTags: (_result, _error, { baseId }) => [{ type: 'Recipe', id: `deploy-${baseId}` }],
    }),

    // Deploy transformer to environment
    deployTransformer: builder.mutation<
      void,
      { appId: string; baseId: string; envId: string; transId: string }
    >({
      query: ({ appId, baseId, envId, transId }) => ({
        url: `${appId}/transformer/${baseId}/environments/${envId}/deploy/${transId}`,
        endpoint: 'transformer',
        method: 'PUT',
      }),
      // Also invalidate 'Schedule' so getAppDeployments refetches — the
      // Schedules page reads version info from that query, and a deploy
      // changes which version a schedule will run with.
      invalidatesTags: (_result, _error, { baseId }) => [
        { type: 'Recipe', id: baseId },
        { type: 'Recipe', id: `deploy-${baseId}` },
        'Schedule',
      ],
    }),

    // Update data processing time threshold
    updateDataProcessingTime: builder.mutation<
      void,
      { versionId: string; maxDataProcTime: string }
    >({
      query: ({ versionId, maxDataProcTime }) => ({
        url: `transformers/${versionId}/max-data-proc-time?maxDataProcTime=${encodeURIComponent(maxDataProcTime)}`,
        endpoint: 'transformer',
        method: 'PUT',
      }),
    }),

    // Delete transformer
    deleteTransformer: builder.mutation<void, string>({
      query: (baseId) => ({
        url: `transformer/delete/${baseId}`,
        method: 'DELETE',
        endpoint: 'transformer',
      }),
      invalidatesTags: (_result, _error, baseId) => [{ type: 'Recipe', id: baseId }, 'Recipe'],
    }),

    // Pause/Resume environment schedules
    pauseEnvironmentSchedules: builder.mutation<
      void,
      { tenantId: string; appId: string; envId: string; isPaused: boolean }
    >({
      query: ({ tenantId, appId, envId, isPaused }) => ({
        url: `${tenantId}/${appId}/pauseenvironment/${envId}`,
        endpoint: 'transformer',
        method: 'PUT',
        body: { pauseSchedules: isPaused },
      }),
      invalidatesTags: (_result, _error, { envId, appId }) => [
        'Schedule',
        { type: 'Connector', id: `env-${envId}` },
        { type: 'Connector', id: `app-${appId}` },
      ],
    }),

    // Get step logs by step GUID (for extended properties / SF bulk jobs)
    getStepLogs: builder.query<
      { extendedProperties?: string; connectionKey?: string },
      { tenantId: string; stepGuid: string }
    >({
      query: ({ tenantId, stepGuid }) => ({
        url: `${tenantId}/logs/step/${stepGuid}`,
        endpoint: 'transformer',
      }),
    }),

    // Get Salesforce bulk job info
    getSfJobInfo: builder.query<
      Record<string, unknown>,
      {
        tenantId: string;
        appId: string;
        envId: string;
        connectionKey: string;
        jobId: string;
      }
    >({
      query: ({ tenantId, appId, envId, connectionKey, jobId }) => ({
        url: `${tenantId}/${appId}/${envId}/${connectionKey}/job/${jobId}/info`,
        endpoint: 'sfAgent',
      }),
    }),

    // Get Salesforce bulk job failed results
    getSfJobFailedResults: builder.query<
      Record<string, unknown>[],
      {
        tenantId: string;
        appId: string;
        envId: string;
        connectionKey: string;
        jobId: string;
      }
    >({
      query: ({ tenantId, appId, envId, connectionKey, jobId }) => ({
        url: `${tenantId}/${appId}/${envId}/${connectionKey}/job/${jobId}/failedResults`,
        endpoint: 'sfAgent',
      }),
    }),

    // Get Salesforce bulk job successful results
    getSfJobSuccessResults: builder.query<
      Record<string, unknown>[],
      {
        tenantId: string;
        appId: string;
        envId: string;
        connectionKey: string;
        jobId: string;
      }
    >({
      query: ({ tenantId, appId, envId, connectionKey, jobId }) => ({
        url: `${tenantId}/${appId}/${envId}/${connectionKey}/job/${jobId}/successfulResults`,
        endpoint: 'sfAgent',
      }),
    }),

    // Get all deployments for an app (for schedule creation + version lookup
    // in the Schedules grid). Tagged with 'Schedule' so it auto-refetches
    // whenever a schedule mutation runs — without this, the version column on
    // the Schedules page can show stale (or empty) data immediately after
    // creating a schedule because the cached deployments don't yet include the
    // baseId|environmentId pair the new schedule references.
    getAppDeployments: builder.query<Deployment[], { tenantId: string; appId: string }>({
      query: ({ tenantId, appId }) => ({
        url: `${tenantId}/${appId}/deployments`,
        endpoint: 'transformer',
      }),
      providesTags: ['Schedule'],
      transformResponse: transformGetAppDeploymentsResponse,
    }),
  }),
});

export const {
  useGetIntegrationTypesQuery,
  useGetAdoptedIntegrationTypesQuery,
  useGetTransformersQuery,
  useGetTransformerVersionsQuery,
  useLazyGetTransformerVersionsQuery,
  useSaveTransformerMutation,
  useUpdateTransformerWithJSONMutation,
  useGetExecutionMetadataByGuidQuery,
  useGetExecutionLogsByGuidQuery,
  useCreateFromTemplateMutation,
  useGetTransformerEventDataQuery,
  usePostTransformerEventDataMutation,
  useGetTransformerEmailsQuery,
  useUpdateTransformerEmailsMutation,
  useUpdateTransformerInfoMutation,
  useGetTriggersQuery,
  useGetAllTriggersQuery,
  useGetTriggerSummaryQuery,
  useGetTriggerDetailsQuery,
  useSaveTriggerMutation,
  useClearTriggerMutation,
  useGetTransformerLogsQuery,
  useGetExecutionCountQuery,
  useGetExecutionDetailsQuery,
  useGetConfigTemplatesQuery,
  useGetWorkspaceExecutionsQuery,
  useGetWorkspaceExecutionCountQuery,
  useGetExecutionStepsQuery,
  useGetExecutionByGuidQuery,
  useExecuteTransformerMutation,
  useStopExecutionMutation,
  useGetExecutionLogsDownloadUrlQuery,
  useGetTransformerDeploymentsQuery,
  useDeployTransformerMutation,
  useUpdateDataProcessingTimeMutation,
  useDeleteTransformerMutation,
  usePauseEnvironmentSchedulesMutation,
  useGetAppDeploymentsQuery,
  useGetStepLogsQuery,
  useLazyGetSfJobInfoQuery,
  useGetSfJobFailedResultsQuery,
  useGetSfJobSuccessResultsQuery,
} = transformersApi;
